/**
 * Compute the SHA-256 of a File using the Web Crypto API. Returns a
 * 64-char hex string. Reads the file's ArrayBuffer once — well within
 * the 50 MB cap enforced server-side.
 */
export async function computeSha256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * PUT the file to the presigned URL with the headers the backend
 * specified. Returns when S3 responds 2xx; throws otherwise.
 *
 * onProgress receives a [0, 1] fraction during upload — XMLHttpRequest
 * is used here (not fetch) because it surfaces upload progress events.
 */
export function uploadToPresignedUrl(
  file: File,
  uploadUrl: string,
  requiredHeaders: Record<string, string>,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    for (const [name, value] of Object.entries(requiredHeaders)) {
      xhr.setRequestHeader(name, value);
    }
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(e.loaded / e.total);
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error("S3 upload network error"));
    xhr.send(file);
  });
}
